"use client"

import { useMemo, useState } from "react"
import { ArrowUpRight, Info, RefreshCw } from "lucide-react"
import {
  StatBorderGrid,
  ToolPageShell,
  ToolProductCarousel,
  ToolShareBar,
} from "@/components/brand-kit"
import type { ToolComponentData } from "@/lib/tool-adapter"
import { readQuantity } from '@/lib/tools/numeric-input'

// Discount tiers preserved verbatim from the legacy calculator. These mirror
// typical manufacturer outlet pricing tiers (per Sears Outlet, GE Appliances
// Outlet, Whirlpool Outlet, and major-retailer scratch-and-dent programs):
//   Minor cosmetic: ~10%
//   Moderate:       ~20%
//   Major:          ~35%
// Location adjustment: front +5%, side ±0%, back/top −5% (less visible).
// Warranty adjustment: full ±0%, limited +5%, none +10%.
// Maximum stacked discount cap: 50%.

type DamageLevel = "minor" | "moderate" | "major"
type Location = "front" | "side" | "back" | "top"
type Warranty = "full" | "limited" | "none"

const RELATED = [
  {
    title: "Refrigerator Cost of Ownership Calculator",
    category: "Energy & Cost",
    description: "True 10-year purchase + energy + maintenance cost.",
    href: "/tools/refrigerator-cost-of-ownership-calculator",
    time: "~45 sec",
  },
  {
    title: "Refrigerator Warranty Comparison",
    category: "Specialty",
    description: "Compare warranty terms across major brands.",
    href: "/tools/refrigerator-warranty-comparison",
    time: "~30 sec",
  },
  {
    title: "Refrigerator Size Calculator",
    category: "Size & Capacity",
    description: "Confirm size before committing to outlet inventory.",
    href: "/tools/refrigerator-size-calculator",
    time: "~30 sec",
  },
  {
    title: "Find Your Fridge",
    category: "Specialty",
    description: "Explore product groups based on your answers.",
    href: "/tools/find-your-fridge",
    time: "~2 min",
  },
]

export function ScratchDentValueCalculatorPageClient(_props: { toolData?: ToolComponentData } = {}) {
  const [retailPrice, setRetailPrice] = useState("1200")
  const [damageLevel, setDamageLevel] = useState<DamageLevel>("minor")
  const [location, setLocation] = useState<Location>("side")
  const [warranty, setWarranty] = useState<Warranty>("full")

  const result = useMemo(() => {
    const price = readQuantity(retailPrice, { min: 50, max: 30000 })
    if (price === null) return null

    let pct = 0
    if (damageLevel === "minor") pct = 10
    else if (damageLevel === "moderate") pct = 20
    else pct = 35

    if (location === "front") pct += 5
    else if (location === "back" || location === "top") pct -= 5

    if (warranty === "limited") pct += 5
    else if (warranty === "none") pct += 10

    pct = Math.min(Math.max(pct, 0), 50)

    const discount = price * (pct / 100)
    const fairPrice = price - discount

    return { price, pct, discount, fairPrice }
  }, [retailPrice, damageLevel, location, warranty])

  return (
    <ToolPageShell
      toolName="Scratch & Dent Value Calculator"
      heroEyebrow="Specialty"
      heroTitlePre="What's a fair price for"
      heroTitleItalic="that scratch-and-dent"
      heroTitlePost="fridge?"
      heroSubtitle="Plug in the retail price and the actual damage. We apply standard manufacturer-outlet discount tiers — by damage level, location, and warranty status — to give you a fair-price target before you negotiate."
      heroMeta={["⏱ ~30 sec", "Manufacturer outlet pricing tiers"]}
      heroShare={<ToolShareBar title="Scratch & Dent Value Calculator" />}
      heroRight={
        <StatBorderGrid
          stats={[
            { value: "10%", label: "Minor cosmetic", note: "Outlet baseline" },
            { value: "20%", label: "Moderate damage", note: "Outlet typical" },
            { value: "35%", label: "Major damage", note: "Outlet typical" },
            { value: "50%", label: "Stacked discount cap", note: "Practical ceiling" },
          ]}
          className="w-full max-w-md md:max-w-none md:w-auto mx-auto md:mx-0"
        />
      }
      faqs={[
        {
          question: "Where do these discount tiers come from?",
          answer:
            "They mirror standard manufacturer-outlet pricing — Sears Outlet, GE Appliances Outlet, Whirlpool Outlet, and major-retailer scratch-and-dent programs typically discount in the 10% / 20% / 35% bands by damage severity.",
        },
        {
          question: "Is the warranty adjustment realistic?",
          answer:
            "Yes. Limited or no warranty meaningfully transfers risk to you, so a deeper discount is warranted. We add 5% for limited and 10% for no warranty — that's at the conservative end of what outlets actually offer.",
        },
        {
          question: "Why does damage location matter?",
          answer:
            "Front damage is always visible; side and top depend on placement; back is hidden against the wall. We add 5% for front damage and subtract 5% for back/top because visible damage carries a real ongoing cost (you see it every day).",
        },
        {
          question: "What's the 50% cap?",
          answer:
            "Practical ceiling. Past 50% off, the math says the unit has functional or structural concerns that go beyond cosmetics — at that point you should be inspecting carefully and asking what's actually wrong.",
        },
        {
          question: "Are scratch-and-dent appliances worth it?",
          answer:
            "Often yes — a 20–30% discount on a current-year ENERGY STAR model is real money. But: scratch-and-dent inventory is usually final sale, no returns. Inspect every function in person before you commit.",
        },
        {
          question: "Should I negotiate beyond the calculator's number?",
          answer:
            "Try. Outlets get more flexible at month-end, on aged inventory, or when multiple cosmetic issues stack. Bring the calculator's fair price as your anchor and ask for an additional 5–10%.",
        },
      ]}
      relatedTools={RELATED}
      cta={{
        title: "Browse new (no-damage) inventory if outlet's too thin",
        body: "See top-freezer refrigerators with full warranty, current pricing, and live retailer availability.",
        buttonLabel: "Browse top-freezer refrigerators",
        buttonHref: "/collections/top-freezer-refrigerators",
      }}
    >
      {/* ============ CALCULATOR ============ */}
      <section className="bg-gray-50 py-8 md:py-12">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 space-y-6">
          <div className="grid lg:grid-cols-2 gap-6 lg:items-stretch">
            {/* Input card */}
            <div className="bg-white rounded-xl p-6 md:p-7 border border-gray-900 flex flex-col">
              <div className="flex items-center gap-3 mb-2">
                <span className="font-mono text-[12px] font-bold tracking-[0.15em] text-[#ff9951]">
                  STEP 01
                </span>
                <div className="flex-1 h-[1px] bg-gray-300" />
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{ fontSize: 28, letterSpacing: "-0.025em", lineHeight: 1.05 }}
              >
                Price.
                <br />
                Damage. Warranty.
              </h2>
              <p className="text-[14px] text-gray-600 mt-2 leading-[1.5]">
                Outlet pricing tiers — apply the same logic stores use, in your favor.
              </p>

              <div className="mt-7 space-y-5">
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Retail price (full)
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[18px] font-bold text-gray-500">$</span>
                    <input
                      type="number"
                      min={50}
                      max={30000}
                      step="0.01"
                      value={retailPrice}
                      onChange={(e) => setRetailPrice(e.target.value)}
                      className="tool-input-clean w-full pl-9 pr-4 py-4 text-[22px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951]"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    />
                  </div>
                  <div className="text-[11px] text-gray-500 mt-1.5 flex items-center gap-1.5">
                    <Info className="w-3.5 h-3.5" /> What it would cost new at full price
                  </div>
                </div>

                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Damage level
                  </label>
                  <select
                    value={damageLevel}
                    onChange={(e) => setDamageLevel(e.target.value as DamageLevel)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  >
                    <option value="minor">Minor cosmetic (small scratches/dents)</option>
                    <option value="moderate">Moderate (visible across room)</option>
                    <option value="major">Major (significant damage)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Damage location
                  </label>
                  <select
                    value={location}
                    onChange={(e) => setLocation(e.target.value as Location)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  >
                    <option value="front">Front (always visible)</option>
                    <option value="side">Side (depends on placement)</option>
                    <option value="back">Back (hidden against wall)</option>
                    <option value="top">Top (rarely visible)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Warranty
                  </label>
                  <select
                    value={warranty}
                    onChange={(e) => setWarranty(e.target.value as Warranty)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  >
                    <option value="full">Full warranty (same as new)</option>
                    <option value="limited">Limited warranty (check exclusions)</option>
                    <option value="none">No warranty (final sale)</option>
                  </select>
                </div>

                <div className="pt-2 text-[11px] uppercase tracking-[0.12em] text-gray-500 font-bold flex items-center gap-1.5">
                  <RefreshCw className="w-3 h-3" /> Updates live as you type
                </div>
              </div>
            </div>

            {/* Result panel */}
            <div className="flex flex-col">
              <div
                className="rounded-2xl p-6 md:p-8 relative overflow-hidden text-white flex-1"
                style={{ background: "linear-gradient(135deg, #0469f0 0%, #0053c4 100%)" }}
              >
                <div
                  aria-hidden
                  className="absolute top-0 right-0 w-[340px] h-[340px] rounded-full pointer-events-none"
                  style={{
                    background: "rgba(255,153,81,0.35)",
                    filter: "blur(60px)",
                    transform: "translate(25%, -35%)",
                  }}
                />
                <div className="relative">
                  <div className="flex items-center justify-between mb-8 flex-wrap gap-2">
                    <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-white/70">
                      Fair price
                    </div>
                    <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] text-white/60">
                      <RefreshCw className="w-3 h-3" /> Updated live
                    </div>
                  </div>
                  <div
                    className="font-black"
                    style={{
                      fontSize: "clamp(36px, 8vw, 88px)",
                      letterSpacing: "-0.045em",
                      lineHeight: 0.9,
                    }}
                  >
                    {`$${result?.fairPrice.toLocaleString("en-US", { maximumFractionDigits: 0 }) ?? "0"}`}
                  </div>
                  <div className="mt-4 text-[14px] text-white/80 leading-[1.5]">
                    {result
                      ? `That's a ${result.pct.toLocaleString("en-US", { maximumFractionDigits: 0 })}% discount — about $${result.discount.toLocaleString("en-US", { maximumFractionDigits: 0 })} off retail. Use it as your negotiation anchor.`
                      : "Enter retail price to see fair-price target."}
                  </div>
                  <div className="grid grid-cols-2 gap-2 sm:gap-3 mt-6 md:mt-8">
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">
                        Discount %
                      </div>
                      <div className="text-[18px] sm:text-[22px] font-black tracking-tight">
                        {result?.pct.toLocaleString("en-US", { maximumFractionDigits: 0 }) ?? "—"}%
                      </div>
                    </div>
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">
                        Dollars off
                      </div>
                      <div className="text-[18px] sm:text-[22px] font-black tracking-tight">
                        {`$${result?.discount.toLocaleString("en-US", { maximumFractionDigits: 0 }) ?? "—"}`}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* What to inspect */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 md:p-7">
            <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-4 text-gray-900">
              Before you commit — inspect in person
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <div className="font-extrabold text-[14px] mb-2">Door seals + hinges</div>
                <p className="text-[13px] text-gray-600 leading-[1.55]">
                  A dent that affects door closure becomes a permanent energy leak — and ruins food. Open and close every door, listen for the seal pulling shut.
                </p>
              </div>
              <div>
                <div className="font-extrabold text-[14px] mb-2">Cooling + ice maker</div>
                <p className="text-[13px] text-gray-600 leading-[1.55]">
                  Ask the outlet to power it on. Confirm both compartments cool, the ice maker cycles, and water dispensing works. Final sale means no recourse later.
                </p>
              </div>
              <div>
                <div className="font-extrabold text-[14px] mb-2">Document the damage</div>
                <p className="text-[13px] text-gray-600 leading-[1.55]">
                  Photograph everything before you sign. If the unit ships with additional damage, those photos are the only evidence you have.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FIRST CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Step 02 · Compare to new"
          title="Top-freezer refrigerators at the value tier"
          description="If outlet inventory is too thin, top-freezer is the cheapest path to a brand-new ENERGY STAR unit."
          collectionType="top-freezer"
          fallbackCollectionType="bottom-freezer"
        />
      </section>

      {/* ============ METHODOLOGY ============ */}
      <section className="bg-gray-50 py-8 md:py-12 border-y border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <div className="grid md:grid-cols-[1fr_1.3fr] gap-8 md:gap-12 items-start">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-3 text-[#ff9951]">
                Under the hood
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{ fontSize: "clamp(24px, 3vw, 36px)", letterSpacing: "-0.03em", lineHeight: 1.05 }}
              >
                Outlet math, <em className="italic text-[#ff9951]">in your favor</em>.
              </h2>
              <p className="text-[14px] text-gray-600 mt-4 max-w-[400px] leading-[1.55]">
                Discount tiers mirror manufacturer outlet pricing programs — Sears, GE, Whirlpool, and
                major-retailer scratch-and-dent inventory price the same three damage bands.
              </p>
            </div>
            <ol className="list-none p-0 m-0 border-t border-gray-900">
              {[
                {
                  n: "01",
                  t: "Base discount by damage level",
                  d: "Minor cosmetic 10%, moderate 20%, major 35% — standard outlet bands.",
                },
                {
                  n: "02",
                  t: "Visibility adjustment",
                  d: "Front +5% (always visible), side ±0%, back / top −5% (hidden or rarely visible).",
                },
                {
                  n: "03",
                  t: "Warranty risk transfer",
                  d: "Limited warranty +5%, no warranty +10%. Final price capped at 50% off — past that, the unit has more than cosmetic issues.",
                },
              ].map((s) => (
                <li
                  key={s.n}
                  className="grid grid-cols-[auto_1fr_auto] gap-4 sm:gap-7 items-center py-5 sm:py-6 border-b border-gray-900"
                >
                  <div className="font-mono text-sm font-bold tracking-[0.05em] min-w-[28px] sm:min-w-[34px] text-[#ff9951]">
                    {s.n}
                  </div>
                  <div>
                    <h3
                      className="text-gray-900"
                      style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}
                    >
                      {s.t}
                    </h3>
                    <p className="mt-1.5 text-[14px] text-gray-600 leading-[1.55]">{s.d}</p>
                  </div>
                  <ArrowUpRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0" />
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* ============ SECOND CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="You might also like"
          title="Side-by-side refrigerators"
          description="Slimmer footprint, eye-level freezer access, broad price range."
          collectionType="side-by-side"
          fallbackCollectionType="top-freezer"
        />
      </section>
    </ToolPageShell>
  )
}
